import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { CONFIGURATION_TABS } from "../../../cloud-connect/ota-update/constants";
import { ListItem } from "@src/types/ota-update";
import { updateModalsCloseDrawerModal } from "@src/reducers/your-reducer";
import { useI18N } from "@src/language/language-context";

type TabType = (typeof CONFIGURATION_TABS)[keyof typeof CONFIGURATION_TABS];

interface UseOTAConfigurationHistoryProps {
  setActiveOTAHistoryTab: React.Dispatch<React.SetStateAction<TabType>>;
  setIsConfigurationLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsBackupLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useOTAConfigurationHistory = ({
  setActiveOTAHistoryTab,
  setIsConfigurationLoading,
  setIsBackupLoading,
}: UseOTAConfigurationHistoryProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<null | ListItem>(null);
  const dispatch = useDispatch();
  const i18n = useI18N();

  const texts = {
    configurationsText: i18n("modules.otaHistory.configurationsText"),
    createBackupText: i18n("modules.otaHistory.createBackupText"),
    backupsText: i18n("modules.otaHistory.backupsText"),
    uploadConfigurationsReleaseText: i18n(
      "modules.otaHistory.uploadConfigurationsReleaseText",
    ),
  };

  const tabLabels: Record<TabType, string> = {
    [CONFIGURATION_TABS.CONFIGURATIONS]: texts.configurationsText,
    [CONFIGURATION_TABS.BACKUPS]: texts.backupsText,
  };

  const labelToKey: Record<string, TabType> = {
    [texts.configurationsText]: CONFIGURATION_TABS.CONFIGURATIONS,
    [texts.backupsText]: CONFIGURATION_TABS.BACKUPS,
  };

  const handleTabChange = (label: string) => {
    const key = labelToKey[label];

    if (!key) {
      return;
    }

    switch (key) {
      case CONFIGURATION_TABS.CONFIGURATIONS:
        setIsConfigurationLoading(true);
        break;

      case CONFIGURATION_TABS.BACKUPS:
        setIsBackupLoading(true);
        break;
    }

    setActiveOTAHistoryTab(key);
  };

  const closeFirmwareModal = () => {
    setIsModalOpen(false);
  };

  const showOTAConfigurationDrawer = () => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: "otaConfigurationDrawerModal",
      }),
    );
  };

  const showOTAConfigurationBackupDrawer = () => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: "otaConfigurationBackupDrawerModal",
      }),
    );
  };

  const getPermission = (activeOTAHistoryTab: TabType) => {
    return activeOTAHistoryTab === CONFIGURATION_TABS.CONFIGURATIONS
      ? "manage-configuration"
      : "manage-configuration-backup";
  };

  return {
    texts,
    tabLabels,
    isModalOpen,
    setIsModalOpen,
    selectedRow,
    setSelectedRow,
    handleTabChange,
    closeFirmwareModal,
    showOTAConfigurationDrawer,
    showOTAConfigurationBackupDrawer,
    getPermission,
  };
};
